import { useState, useEffect, useRef } from 'react';
import useSWR from 'swr';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Combobox, type ComboboxOption } from '@/components/ui/combobox';
import { PageHeader } from '@/components/ui/page-header';
import { Loader2 } from 'lucide-react';
import { XMarkIcon, PlusIcon } from '@heroicons/react/24/outline';

const fetcher = (url: string) => fetch(url, { credentials: 'include' }).then((res) => res.json());

const toOptions = (values: readonly string[]): ComboboxOption[] =>
  values.map(v => ({ value: v, label: v }));

// Apollo's `organizationNumEmployeesRanges[]` accepts these buckets exactly.
// Fixed enum — no custom values allowed in the picker.
const COMPANY_SIZE_OPTIONS: ComboboxOption[] = toOptions([
  '1-10', '11-50', '51-200', '201-500', '501-1000', '1001-5000', '5001-10000', '10001+',
]);

// Apollo `personSeniorities[]` taxonomy. Far broader than title strings —
// "Chief Technology Officer" and "CTO" both classify as `c_suite`.
const SENIORITY_OPTIONS: ComboboxOption[] = [
  { value: 'owner', label: 'Owner' },
  { value: 'founder', label: 'Founder' },
  { value: 'c_suite', label: 'C-Suite' },
  { value: 'partner', label: 'Partner' },
  { value: 'vp', label: 'VP' },
  { value: 'head', label: 'Head' },
  { value: 'director', label: 'Director' },
  { value: 'manager', label: 'Manager' },
  { value: 'senior', label: 'Senior' },
  { value: 'entry', label: 'Entry' },
  { value: 'intern', label: 'Intern' },
];

// Apollo `personDepartmentOrSubdepartments[]` taxonomy (top-level only).
const DEPARTMENT_OPTIONS: ComboboxOption[] = [
  { value: 'engineering', label: 'Engineering' },
  { value: 'information_technology', label: 'IT' },
  { value: 'product', label: 'Product' },
  { value: 'design', label: 'Design' },
  { value: 'sales', label: 'Sales' },
  { value: 'marketing', label: 'Marketing' },
  { value: 'operations', label: 'Operations' },
  { value: 'finance', label: 'Finance' },
  { value: 'legal', label: 'Legal' },
  { value: 'human_resources', label: 'Human Resources' },
  { value: 'consulting', label: 'Consulting' },
  { value: 'media_communication', label: 'Media & Communication' },
  { value: 'education', label: 'Education' },
  { value: 'support', label: 'Support' },
  { value: 'real_estate', label: 'Real Estate' },
  { value: 'logistics', label: 'Logistics' },
];

// Suggestion lists below seed the search dropdowns with sensible starting
// options. The combobox's `allowCustom` prop lets users type and Enter to
// add anything not in the list, so these aren't restrictive — just helpful.
const ROLE_SUGGESTIONS: ComboboxOption[] = toOptions([
  'CEO', 'Founder', 'Co-Founder', 'CTO', 'CFO', 'COO', 'CMO',
  'VP Engineering', 'VP Sales', 'VP Marketing', 'VP Product', 'VP Operations',
  'Head of Engineering', 'Head of Sales', 'Head of Marketing', 'Head of Product',
  'Director of Engineering', 'Director of Sales', 'Director of Marketing',
  'Engineering Manager', 'Product Manager', 'Sales Manager', 'Operations Manager',
]);

const POST_TOWN_SUGGESTIONS: ComboboxOption[] = toOptions([
  'LONDON', 'BIRMINGHAM', 'MANCHESTER', 'LEEDS', 'GLASGOW', 'EDINBURGH',
  'LIVERPOOL', 'BRISTOL', 'SHEFFIELD', 'NEWCASTLE UPON TYNE', 'NOTTINGHAM',
  'SOUTHAMPTON', 'PORTSMOUTH', 'CARDIFF', 'BELFAST', 'OXFORD', 'CAMBRIDGE',
  'BRIGHTON', 'READING', 'COVENTRY', 'LEICESTER', 'YORK', 'BATH', 'EXETER',
  'NORWICH', 'PLYMOUTH', 'ABERDEEN', 'DUNDEE', 'SWANSEA',
]);

const COUNTY_SUGGESTIONS: ComboboxOption[] = toOptions([
  'Greater London', 'Greater Manchester', 'West Midlands', 'West Yorkshire',
  'Merseyside', 'South Yorkshire', 'Tyne and Wear', 'Surrey', 'Hampshire',
  'Kent', 'Essex', 'Hertfordshire', 'Berkshire', 'Buckinghamshire',
  'Oxfordshire', 'Cambridgeshire', 'Norfolk', 'Suffolk', 'Devon', 'Cornwall',
  'Somerset', 'Dorset', 'East Sussex', 'West Sussex', 'Gloucestershire',
  'Warwickshire', 'Leicestershire', 'Nottinghamshire', 'Derbyshire',
  'Lancashire', 'Cheshire', 'Staffordshire', 'Lothian', 'Strathclyde',
]);

export default function WorkspaceSettings() {
  const { data: team, mutate } = useSWR('/api/team', fetcher);
  const [isSaving, setIsSaving] = useState(false);

  const [websiteUrl, setWebsiteUrl] = useState('');
  const [businessSummary, setBusinessSummary] = useState('');
  const [offerings, setOfferings] = useState<{ name: string; description: string }[]>([]);
  // Workspace ICP — hard filters, every one wired into Apollo's "Find People".
  // Six multi-select comboboxes (string[]) for a consistent feel. Seniorities
  // and departments are Apollo's own taxonomy and the primary precision lever.
  const [icpSeniorities, setIcpSeniorities] = useState<string[]>([]);
  const [icpDepartments, setIcpDepartments] = useState<string[]>([]);
  const [icpRoleKeywords, setIcpRoleKeywords] = useState<string[]>([]);
  const [icpPostTowns, setIcpPostTowns] = useState<string[]>([]);
  const [icpCounties, setIcpCounties] = useState<string[]>([]);
  const [icpCompanySizes, setIcpCompanySizes] = useState<string[]>([]);
  const [icpCompanyKeywords, setIcpCompanyKeywords] = useState<string[]>([]);

  // Refs that mirror state so handleSave can read post-blur values without
  // hitting the stale-closure trap (Save's click captures pre-blur state).
  const icpSenioritiesRef = useRef(icpSeniorities);
  const icpDepartmentsRef = useRef(icpDepartments);
  const icpRoleKeywordsRef = useRef(icpRoleKeywords);
  const icpPostTownsRef = useRef(icpPostTowns);
  const icpCountiesRef = useRef(icpCounties);
  const icpCompanySizesRef = useRef(icpCompanySizes);
  const icpCompanyKeywordsRef = useRef(icpCompanyKeywords);
  useEffect(() => { icpSenioritiesRef.current = icpSeniorities; }, [icpSeniorities]);
  useEffect(() => { icpDepartmentsRef.current = icpDepartments; }, [icpDepartments]);
  useEffect(() => { icpRoleKeywordsRef.current = icpRoleKeywords; }, [icpRoleKeywords]);
  useEffect(() => { icpPostTownsRef.current = icpPostTowns; }, [icpPostTowns]);
  useEffect(() => { icpCountiesRef.current = icpCounties; }, [icpCounties]);
  useEffect(() => { icpCompanySizesRef.current = icpCompanySizes; }, [icpCompanySizes]);
  useEffect(() => { icpCompanyKeywordsRef.current = icpCompanyKeywords; }, [icpCompanyKeywords]);

  // Holds the latest team object without forcing autosave to re-run when it
  // changes — avoids a transient state-vs-team mismatch on the render where
  // SWR has updated team but the load-from-team effect's setState hasn't
  // applied yet, which would otherwise flash a bogus "Saving…".
  const teamRef = useRef(team);
  useEffect(() => { teamRef.current = team; }, [team]);

  const [icpSaveStatus, setIcpSaveStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');
  const [outreachGoal, setOutreachGoal] = useState('');
  const [messagingTone, setMessagingTone] = useState('professional');

  useEffect(() => {
    if (team) {
      setWebsiteUrl(team.websiteUrl || '');
      setBusinessSummary(team.businessSummary || '');
      setOfferings(Array.isArray(team.offerings) ? team.offerings : []);
      setIcpSeniorities(Array.isArray(team.icp?.seniorities) ? team.icp.seniorities : []);
      setIcpDepartments(Array.isArray(team.icp?.departments) ? team.icp.departments : []);
      setIcpRoleKeywords(Array.isArray(team.icp?.roleKeywords) ? team.icp.roleKeywords : []);
      setIcpPostTowns(Array.isArray(team.icp?.postTowns) ? team.icp.postTowns : []);
      setIcpCounties(Array.isArray(team.icp?.counties) ? team.icp.counties : []);
      setIcpCompanySizes(Array.isArray(team.icp?.companySizeRanges) ? team.icp.companySizeRanges : []);
      setIcpCompanyKeywords(Array.isArray(team.icp?.companyKeywords) ? team.icp.companyKeywords : []);
      setOutreachGoal(team.outreachGoal || '');
      setMessagingTone(team.messagingTone || 'professional');
    }
  }, [team]);

  // Auto-save the ICP block whenever the user changes it. Debounced 600ms so
  // rapid selections coalesce into one PATCH. Reads `team` via a ref so SWR
  // refetches don't re-trigger the effect — only genuine state changes do.
  // The "Saving…" status is set inside the timeout, not eagerly, so a quickly
  // cleaned-up effect tick doesn't flash status the user can see.
  useEffect(() => {
    const t = teamRef.current;
    if (!t) return;
    const current = JSON.stringify({
      seniorities: icpSeniorities,
      departments: icpDepartments,
      roleKeywords: icpRoleKeywords,
      postTowns: icpPostTowns,
      counties: icpCounties,
      companySizeRanges: icpCompanySizes,
      companyKeywords: icpCompanyKeywords,
    });
    const onServer = JSON.stringify({
      seniorities: Array.isArray(t.icp?.seniorities) ? t.icp.seniorities : [],
      departments: Array.isArray(t.icp?.departments) ? t.icp.departments : [],
      roleKeywords: Array.isArray(t.icp?.roleKeywords) ? t.icp.roleKeywords : [],
      postTowns: Array.isArray(t.icp?.postTowns) ? t.icp.postTowns : [],
      counties: Array.isArray(t.icp?.counties) ? t.icp.counties : [],
      companySizeRanges: Array.isArray(t.icp?.companySizeRanges) ? t.icp.companySizeRanges : [],
      companyKeywords: Array.isArray(t.icp?.companyKeywords) ? t.icp.companyKeywords : [],
    });
    if (current === onServer) return;

    const handle = setTimeout(async () => {
      setIcpSaveStatus('saving');
      try {
        const res = await fetch('/api/workspace', {
          method: 'PATCH',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            icp: {
              seniorities: icpSeniorities,
              departments: icpDepartments,
              roleKeywords: icpRoleKeywords,
              postTowns: icpPostTowns,
              counties: icpCounties,
              companySizeRanges: icpCompanySizes,
              companyKeywords: icpCompanyKeywords,
            },
          }),
        });
        if (!res.ok) {
          const detail = await res.text().catch(() => '');
          throw new Error(`HTTP ${res.status} ${detail.slice(0, 200)}`);
        }
        mutate();
        setIcpSaveStatus('saved');
        setTimeout(() => setIcpSaveStatus('idle'), 1500);
      } catch (err) {
        setIcpSaveStatus('error');
        toast.error(`ICP save failed: ${(err as Error).message}`);
      }
    }, 600);
    return () => clearTimeout(handle);
    // mutate is stable from SWR; team accessed via ref. Intentional.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [icpSeniorities, icpDepartments, icpRoleKeywords, icpPostTowns, icpCounties, icpCompanySizes, icpCompanyKeywords]);

  const handleSave = async () => {
    setIsSaving(true);
    // Flush any focused combobox/input so its onBlur commits pending text into
    // state. Then yield to the event loop so React's commit phase runs and
    // the useEffects that mirror state into refs fire — meaning the refs
    // below have the post-blur values even though closure variables don't.
    if (document.activeElement instanceof HTMLElement) document.activeElement.blur();
    await new Promise(r => setTimeout(r, 0));
    await new Promise(r => setTimeout(r, 0));

    try {
      const res = await fetch('/api/workspace', {
        method: 'PATCH',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          websiteUrl: websiteUrl || null,
          businessSummary: businessSummary || null,
          offerings: offerings.length > 0 ? offerings : null,
          icp: {
            seniorities: icpSenioritiesRef.current,
            departments: icpDepartmentsRef.current,
            roleKeywords: icpRoleKeywordsRef.current,
            postTowns: icpPostTownsRef.current,
            counties: icpCountiesRef.current,
            companySizeRanges: icpCompanySizesRef.current,
            companyKeywords: icpCompanyKeywordsRef.current,
          },
          outreachGoal: outreachGoal || null,
          messagingTone: messagingTone || null,
        }),
      });
      if (!res.ok) {
        const detail = await res.text().catch(() => '');
        throw new Error(`HTTP ${res.status} ${detail.slice(0, 200)}`);
      }
      mutate();
      toast.success('Workspace profile saved');
    } catch (err) {
      toast.error(`Failed to save: ${(err as Error).message}`);
    } finally {
      setIsSaving(false);
    }
  };

  const addOffering = () => setOfferings(prev => [...prev, { name: '', description: '' }]);
  const removeOffering = (index: number) => setOfferings(prev => prev.filter((_, i) => i !== index));
  const updateOffering = (index: number, field: 'name' | 'description', value: string) => {
    setOfferings(prev => prev.map((o, i) => i === index ? { ...o, [field]: value } : o));
  };

  return (
    <>
      <PageHeader title="AI Context" className="mt-page-top mb-header-gap" />

      <div className="px-gutter pb-10 max-w-3xl space-y-6">
        {/* Business Profile */}
        <div className="bg-card border border-border rounded-xl p-6">
          <h3 className="text-base font-semibold mb-5">Business Profile</h3>
          <div className="space-y-4">
            <Input
              value={websiteUrl}
              onChange={(e) => setWebsiteUrl(e.target.value)}
              inlineLabel="Website"
              placeholder="yourcompany.com"
            />
            <Textarea
              value={businessSummary}
              onChange={(e) => setBusinessSummary(e.target.value)}
              placeholder="Describe what your business does and its value proposition..."
              className="min-h-[100px]"
            />
          </div>
        </div>

        {/* Offerings */}
        <div className="bg-card border border-border rounded-xl p-6">
          <h3 className="text-base font-semibold mb-5">Offerings</h3>
          <div className="space-y-4">
            {offerings.map((offering, i) => (
              <div key={i} className="p-4 bg-surface-sunken rounded-xl space-y-3 relative group">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-medium text-muted-foreground">Offering {i + 1}</span>
                  {offerings.length > 1 && (
                    <button
                      onClick={() => removeOffering(i)}
                      className="w-6 h-6 rounded-full bg-surface-track hover:bg-danger-surface-strong text-muted-foreground hover:text-danger-text transition-colors flex items-center justify-center cursor-pointer"
                    >
                      <XMarkIcon className="h-3 w-3" />
                    </button>
                  )}
                </div>
                <Input
                  value={offering.name}
                  onChange={(e) => updateOffering(i, 'name', e.target.value)}
                  placeholder="Offering name"
                  inlineLabel="Name"
                  className="w-full"
                  style={{ paddingLeft: '6.4rem' }}
                />
                <Input
                  value={offering.description}
                  onChange={(e) => updateOffering(i, 'description', e.target.value)}
                  placeholder="What problem does this solve for clients?"
                  inlineLabel="Solves"
                  className="w-full"
                  style={{ paddingLeft: '6.4rem' }}
                />
              </div>
            ))}
            <Button
              variant="secondary"
              size="sm"
              onClick={addOffering}
              className="gap-1.5"
            >
              <PlusIcon className="h-3.5 w-3.5" />
              Add offering
            </Button>
          </div>
        </div>

        {/* ICP */}
        <div className="bg-card border border-border rounded-xl p-6">
          <div className="flex items-center justify-between mb-1">
            <h3 className="text-base font-semibold">Ideal Customer Profile</h3>
            {icpSaveStatus !== 'idle' && (
              <span className={`text-xs ${
                icpSaveStatus === 'saving' ? 'text-muted-foreground' :
                icpSaveStatus === 'saved' ? 'text-success-foreground-alt' :
                'text-danger-foreground'
              }`}>
                {icpSaveStatus === 'saving' && 'Saving…'}
                {icpSaveStatus === 'saved' && 'Saved'}
                {icpSaveStatus === 'error' && 'Save failed'}
              </span>
            )}
          </div>
          <p className="text-xs text-muted-foreground mb-5">
            Hard filters used by Auto Prospect. Seniority + Department are Apollo's own taxonomy and the most reliable lever — title keywords are an optional override for finer slicing.
          </p>
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium mb-1.5 block">Seniority</label>
              <Combobox
                options={SENIORITY_OPTIONS}
                value={icpSeniorities}
                onChange={setIcpSeniorities}
                placeholder="Pick one or more seniority levels…"
              />
            </div>

            <div>
              <label className="text-sm font-medium mb-1.5 block">Department</label>
              <Combobox
                options={DEPARTMENT_OPTIONS}
                value={icpDepartments}
                onChange={setIcpDepartments}
                placeholder="Pick one or more departments…"
              />
            </div>

            <div>
              <label className="text-sm font-medium mb-1.5 block">Role keywords (optional)</label>
              <Combobox
                options={ROLE_SUGGESTIONS}
                value={icpRoleKeywords}
                onChange={setIcpRoleKeywords}
                placeholder="Optional title substring filter…"
                allowCustom
              />
            </div>

            <div>
              <label className="text-sm font-medium mb-1.5 block">Post towns</label>
              <Combobox
                options={POST_TOWN_SUGGESTIONS}
                value={icpPostTowns}
                onChange={setIcpPostTowns}
                placeholder="Search or type a UK post town…"
                allowCustom
              />
            </div>

            <div>
              <label className="text-sm font-medium mb-1.5 block">Counties</label>
              <Combobox
                options={COUNTY_SUGGESTIONS}
                value={icpCounties}
                onChange={setIcpCounties}
                placeholder="Search or type a UK county…"
                allowCustom
              />
            </div>

            <div>
              <label className="text-sm font-medium mb-1.5 block">Company size</label>
              <Combobox
                options={COMPANY_SIZE_OPTIONS}
                value={icpCompanySizes}
                onChange={setIcpCompanySizes}
                placeholder="Pick one or more employee-count ranges…"
              />
            </div>

            <div>
              <label className="text-sm font-medium mb-1.5 block">Company keywords</label>
              <Combobox
                options={[]}
                value={icpCompanyKeywords}
                onChange={setIcpCompanyKeywords}
                placeholder="e.g. agency, lead generation, outreach…"
                allowCustom
              />
              <p className="text-xs text-muted-foreground mt-1.5">
                Free-text keywords that filter on the prospect's company. Apollo searches across the company name, description and tagged industry. Use this to target a vertical that isn't in Apollo's fixed list (e.g. "agency", "lead generation"). Multiple keywords are OR'd.
              </p>
            </div>
          </div>
        </div>

        {/* Outreach Preferences */}
        <div className="bg-card border border-border rounded-xl p-6">
          <h3 className="text-base font-semibold mb-5">Outreach Preferences</h3>
          <div className="space-y-5">
            <Textarea
              value={outreachGoal}
              onChange={(e) => setOutreachGoal(e.target.value)}
              placeholder="What's the main goal of your outreach? e.g. Book discovery calls with potential clients"
              className="min-h-[80px]"
            />
            <div>
              <p className="text-sm font-medium mb-3">Messaging Tone</p>
              <div className="grid grid-cols-2 gap-2">
                {[
                  { value: 'professional', label: 'Professional', description: 'Formal, business-appropriate' },
                  { value: 'casual', label: 'Casual', description: 'Friendly, conversational' },
                  { value: 'consultative', label: 'Consultative', description: 'Advisory, value-focused' },
                  { value: 'direct', label: 'Direct', description: 'Short, to the point' },
                ].map((tone) => (
                  <button
                    key={tone.value}
                    type="button"
                    onClick={() => setMessagingTone(tone.value)}
                    className={`text-left p-3 rounded-md border text-sm transition-colors ${
                      messagingTone === tone.value
                        ? 'border-info-solid bg-surface-selected-accent'
                        : 'border-border/50 hover:border-border'
                    }`}
                  >
                    <span className="font-medium">{tone.label}</span>
                    <span className="block text-xs text-muted-foreground mt-0.5">{tone.description}</span>
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Save */}
        <div className="flex justify-end">
          <Button variant="primary" onClick={handleSave} disabled={isSaving}>
            {isSaving ? <><Loader2 className="h-4 w-4 animate-spin mr-2" />Saving...</> : 'Save Changes'}
          </Button>
        </div>
      </div>
    </>
  );
}
